import { useCallback, useRef, useState, useEffect } from 'react';

export type AsyncState<T, E = any> = {
  data: T | null;
  loading: boolean;
  error: E | null;
  // derived
  isLoading: boolean;
  isError: boolean;
  hasData: boolean;
  // setters
  setData: (value: T | null) => void;
  setError: (err: E | null) => void;
  setLoading: (v: boolean) => void;
  // helpers
  start: () => void;
  success: (value: T) => void;
  failure: (err: E) => void;
  reset: (options?: {
    data?: T | null;
    loading?: boolean;
    error?: E | null;
  }) => void;
};

export function useAsyncState<T = unknown, E = any>(initial?: {
  data?: T | null;
  loading?: boolean;
  error?: E | null;
}): AsyncState<T, E> {
  const {
    data: initData = null,
    loading: initLoading = false,
    error: initError = null,
  } = initial || {};

  const [data, setDataState] = useState<T | null>(initData);
  const [loading, setLoadingState] = useState<boolean>(initLoading);
  const [error, setErrorState] = useState<E | null>(initError);

  // mounted ref to avoid setting state after unmount
  const mountedRef = useRef<boolean>(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSet = useCallback(
    <K extends unknown>(setter: (v: K) => void, value: K) => {
      if (!mountedRef.current) return;
      setter(value);
    },
    [],
  );

  const setData = useCallback(
    (value: T | null) => safeSet(setDataState, value),
    [safeSet],
  );

  const setError = useCallback(
    (err: E | null) => safeSet(setErrorState, err),
    [safeSet],
  );

  const setLoading = useCallback(
    (v: boolean) => safeSet(setLoadingState, v),
    [safeSet],
  );

  const start = useCallback(() => {
    setError(null);
    setLoading(true);
  }, [setError, setLoading]);

  const success = useCallback(
    (value: T) => {
      setData(value);
      setError(null);
      setLoading(false);
    },
    [setData, setError, setLoading],
  );

  const failure = useCallback(
    (err: E) => {
      setError(err);
      setLoading(false);
    },
    [setError, setLoading],
  );

  const reset = useCallback(
    (options?: { data?: T | null; loading?: boolean; error?: E | null }) => {
      const {
        data: d = initData,
        loading: l = initLoading,
        error: e = initError,
      } = options || {};
      if (!mountedRef.current) return;
      setDataState(d);
      setLoadingState(l);
      setErrorState(e);
    },
    [initData, initLoading, initError],
  );

  return {
    data,
    loading,
    error,
    // derived
    isLoading: loading,
    isError: error !== null,
    hasData: data !== null,
    // setters
    setData,
    setError,
    setLoading,
    // helpers
    start,
    success,
    failure,
    reset,
  };
}
